import { Component, Inject, OnInit, OnDestroy } from '@angular/core';
import { TreeNode } from 'primeng/api';
import { MainFacade } from '../../../main.facade';
import { IFileInfo } from '../../../models/file-info';
import { FormBuilder, Validators } from '@angular/forms';
import { RequestFactory } from '../../../models/request.factory';
import { Subscription } from 'rxjs';
import { YoloModels } from '../../../models/request';
import { SubmissionStateService } from '../../../../services/submission-state.service';
import { ProcessComponentBase } from '../process.component.base';
import { VISUALIZER, IVisualizer } from '@jax-data-science/sci-image-visualizer';

@Component({
  selector: 'yolosegdetect',
  templateUrl: './yolosegdetect.component.html',
  styleUrls: ['./yolosegdetect.component.scss'],
})
export class YolosegdetectComponent extends ProcessComponentBase implements OnInit, OnDestroy {

  public inputImages: IFileInfo[] = [];
  models = Object.values(YoloModels).map(model => model.name);

  private levelSizeSubscription!: Subscription;

  public levels = [{ shape: '0: 512x512', level: 0 }];

  constructor(mainFacade: MainFacade,
              @Inject(VISUALIZER) private plotService: IVisualizer,
              private fb: FormBuilder,
							submissionState: SubmissionStateService) {
		super('Yolo Segmentation and Detection', submissionState, mainFacade);
	}

  ngOnInit(): void {
    this.configForm();
    this.mainFacade.getSelectedNode$().subscribe({
      next: (node) => {
        if (node == undefined || node.data == undefined) {
          this.bucket = undefined;
        } else {
          this.setImagesInput(node);
        }
      },
      error: (err) => {
        console.error(`Error with node selection: ${err}`);
      },
    });
    this.mainFacade.getValidationMessage$().subscribe({
      next: (message) => {
        this.validationMessage = message;
      }
    });
    this.levelSizeSubscription = this.plotService.getImageMeta().subscribe(imageMeta => {
      this.levels = [];
      for (let i = 0; i < imageMeta.length; i++) {
        this.levels.push({ shape: `${ i }: ${ imageMeta[i].x }x${ imageMeta[i].y }`, level: i });
      }

      const levelDisabled = this.levels.length !== 1;
      if (levelDisabled) {
        this.form.get('level')?.enable();
        this.form.get('downSamplingFactor')?.disable();
      } else {
        this.form.get('level')?.disable();
        this.form.get('downSamplingFactor')?.enable();
      }
    });
  }

  ngOnDestroy(): void {
    if (this.levelSizeSubscription) {
      this.levelSizeSubscription.unsubscribe();
    }
  }

  setImagesInput(node: TreeNode<IFileInfo>) {
    this.inputImages = [];
    // if folder
    if (node && node.children && node.children.length > 0) {
      node.children?.forEach(child => {
        if (child.data && this.isImageExtension(child.data.name)) {
          this.inputImages.push(child.data);
        }
      });
    } else if (node && node.children && node.children.length == 0) {
      // if file
      if (node.data && this.isImageExtension(node.data.name)) {
        this.inputImages.push(node.data);
      }
    }
    this.bucket = node.data?.project.bucket;
    if (node.leaf) {
      this.path = node.parent?.data?.relPath;
    } else {
      this.path = node.data?.relPath;
    }

  }

  isImageExtension(name: string) {
    const extension = name.toLowerCase().split('.').pop();
    return extension === 'tif' || extension === 'tiff' || extension === 'png' || extension === 'jpg' || extension === 'jpeg' || extension === 'ndpi';
  }
  configForm() {
    this.form = this.fb.group({
      modelFile: [YoloModels.YOLOV8_OPTIC_NERVE, Validators.required],
      level: [0],
      downSamplingFactor: [5, Validators.required],
      overlapX: [0, Validators.required],
      overlapY: [0, Validators.required],
      confidence: [0.6, Validators.required],
      iouThreshold: [0.5, Validators.required],
      nmsThreshold: [0.3, Validators.required],
      saveSegment: [false],
      outputFolder: ['yolo_results', Validators.required],
      editOutputFolder: [false],
			db_upsert: [false]
    });
    this.persistForm();
  }

  onModelChange(event: any) {
    const selectedModelName = event.value;
    const selectedModel = Object.values(YoloModels).find(model => model.name === selectedModelName);
    if (selectedModel) {
      this.form.patchValue({
        downSamplingFactor: selectedModel.downsamplingFactor,
        overlapX: selectedModel.overlapX,
        overlapY: selectedModel.overlapY,
        confidence: selectedModel.confidence,
        iouThreshold: selectedModel.iouThreshold,
        nmsThreshold: selectedModel.nmsThreshold
      });
    }
  }

  onSubmit() {
    super.submit(this.buildRequest());
  }

  onCreateRule() {
    super.createRule(this.buildRequest());
  }

  private buildRequest() {
    const formValue = this.form.value;
    const bucket = this.bucket;
    const parentPath = this.path;
    return RequestFactory.getYolosegdetectRequest(formValue, bucket,
      parentPath, this.inputImages);
  }

}
